import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#050508] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white mb-6 transition-colors text-sm">
          <ArrowLeft size={14} /><span className="font-body">Back to Home</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/15 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#8B5CF6]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">Privacy Policy</h1>
              <p className="font-body text-white/40 text-sm">Last updated: August 2026</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 md:p-8 space-y-6">
            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">1. Introduction</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Monk Algo ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our trading software services, including TradingView indicators, MetaTrader 5 Expert Advisors, and NinjaTrader 8 strategies.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">2. Information We Collect</h2>
              <div className="space-y-3">
                <div>
                  <h3 className="font-heading font-medium text-white/80 text-sm mb-1">Personal Information</h3>
                  <p className="font-body text-white/60 text-sm leading-relaxed">
                    When you subscribe to our services, we may collect: name, email address, phone number, billing address, and payment information processed securely through Razorpay.
                  </p>
                </div>
                <div>
                  <h3 className="font-heading font-medium text-white/80 text-sm mb-1">Usage Data</h3>
                  <p className="font-body text-white/60 text-sm leading-relaxed">
                    We automatically collect certain information when you visit our website, including IP address, browser type, device information, pages visited, and time spent on pages.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">3. How We Use Your Information</h2>
              <ul className="space-y-2">
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  To provide and maintain our software subscription services
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  To process payments and send transaction confirmations
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  To send product updates, support communications, and important notices
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  To improve our website and services based on usage patterns
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  To comply with legal obligations
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">4. Payment Security</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                All payment transactions are processed through Razorpay, a PCI-DSS compliant payment gateway. We do not store your complete credit/debit card details on our servers. Razorpay's privacy policy governs the collection and use of payment information.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">5. Data Sharing and Disclosure</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-3">
                We do not sell, trade, or rent your personal information to third parties. We may share information with:
              </p>
              <ul className="space-y-2">
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#007AFF] mt-1">•</span>
                  Payment processors (Razorpay) to complete transactions
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#007AFF] mt-1">•</span>
                  Service providers who assist in operating our website
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#007AFF] mt-1">•</span>
                  Legal authorities when required by law
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">6. Data Retention</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                We retain your personal information for as long as your account is active or as needed to provide services. We may retain certain information as required by law or for legitimate business purposes.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">7. Your Rights</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                You have the right to access, update, or delete your personal information. To exercise these rights, please contact us at support@monkalgo.com. We will respond to your request within 30 days.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">8. Cookies</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                We use cookies and similar tracking technologies to enhance your browsing experience. You can set your browser to refuse cookies, but some features of our website may not function properly.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">9. Contact Us</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                If you have questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-3 p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="font-body text-white/70 text-sm">Email: support@monkalgo.com</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
